import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Package, LogOut, Gift, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Account = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManaging, setIsManaging] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("pas encore abonné");
  const [referralCode, setReferralCode] = useState<string>("");
  const [referralCount, setReferralCount] = useState<number>(0);
  const [referralPoints, setReferralPoints] = useState<number>(0);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/connexion');
      } else {
        setUser(session.user);
        
        // Check subscription status
        const { data: subscribers, error: subError } = await supabase
          .from('subscribers')
          .select('subscription_status')
          .eq('email', session.user.email);
        
        console.log('Subscribers data:', subscribers);
        console.log('Subscribers error:', subError);
        console.log('User email:', session.user.email);
        
        if (subscribers && subscribers.length > 0 && subscribers[0].subscription_status === 'active') {
          setSubscriptionStatus('actif');
        } else {
          setSubscriptionStatus('pas encore abonné');
        }

        // Get or create referral code
        let { data: referralData, error: referralError } = await supabase
          .from('referral_codes')
          .select('referral_code')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (!referralData && !referralError) {
          // Generate new referral code
          const { data: newCode, error: generateError } = await supabase
            .rpc('generate_referral_code');
          
          if (!generateError && newCode) {
            // Insert the new referral code
            const { error: insertError } = await supabase
              .from('referral_codes')
              .insert({ user_id: session.user.id, referral_code: newCode });
            
            if (!insertError) {
              setReferralCode(newCode);
            }
          }
        } else if (referralData) {
          setReferralCode(referralData.referral_code);
        }

        // Get referral count
        const { data: referrals, error: referralsError } = await supabase
          .from('referrals')
          .select('id, status, created_at')
          .eq('referrer_user_id', session.user.id);

        if (referrals && !referralsError) {
          setReferralCount(referrals.length);
          
          // Calculate points: 1 point per month for each active referral
          let totalPoints = 0;
          referrals.forEach(referral => {
            if (referral.status === 'active') {
              const monthsActive = Math.floor(
                (new Date().getTime() - new Date(referral.created_at).getTime()) / (1000 * 60 * 60 * 24 * 30)
              );
              totalPoints += monthsActive;
            }
          });
          setReferralPoints(totalPoints);
        }
      }
      setIsLoading(false);
    };
    checkUser();
  }, [navigate]);

  const handleManageSubscription = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (!session?.access_token) {
      toast({
        title: "Erreur",
        description: "Utilisateur non connecté",
        variant: "destructive"
      });
      return;
    }

    setIsManaging(true);

    try {
      const res = await fetch("https://qudvztcondkuuiqjascx.supabase.co/functions/v1/stripe_create_portal", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${session.access_token}`,
          "Content-Type": "application/json"
        }
      });

      const result = await res.json();

      if (res.ok && result.url) {
        window.location.href = result.url;
      } else {
        toast({
          title: "Erreur",
          description: result.error || "Impossible d'accéder au portail",
          variant: "destructive"
        });
      }

    } catch (err) {
      console.error("Erreur:", err);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setIsManaging(false);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Erreur",
        description: "Impossible de se déconnecter",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
      navigate('/');
    }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <p>Chargement...</p>
    </div>;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-2">
                Mon espace Yumeli
              </h1>
              <p className="text-xl text-muted-foreground">
                Bienvenue {user.user_metadata?.first_name || user.email}
              </p>
            </div>
            <Button onClick={handleSignOut} variant="outline" className="gap-2">
              <LogOut className="w-4 h-4" />
              Se déconnecter
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {/* Abonnement actuel */}
            <Card className="p-6 shadow-warm">
              <div className="flex items-center gap-3 mb-6">
                <Package className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold text-foreground">Mon abonnement</h2>
              </div>

              <div className="space-y-4">
                
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Formule</span>
                  <span className="font-semibold">
                    {subscriptionStatus === 'actif' ? 'Abonnement mensuel' : 'Non-abonné'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Fréquence d'envoi de box</span>
                  <span className="font-semibold">Tous les mois (1 activité par box)</span>
                </div>

                <Separator className="my-4" />
                
                <div className="space-y-2">
                  {subscriptionStatus === 'actif' ? (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={handleManageSubscription}
                      disabled={isManaging}
                    >
                      {isManaging ? "Chargement..." : "Gérer mon abonnement"}
                    </Button>
                  ) : (
                    <p className="text-center text-muted-foreground">
                      Vous n'avez pas encore d'abonnement actif.
                    </p>
                  )}
                </div>
              </div>
            </Card>

            {/* Mes parrainages */}
            <Card className="p-6 shadow-warm">
              <div className="flex items-center gap-3 mb-6">
                <Gift className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-semibold text-foreground">Mes parrainages</h2>
              </div>

              <div className="space-y-6">
                {/* Code de parrainage */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Mon code de parrainage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-2xl font-bold text-primary tracking-wider">
                      {referralCode || 'CHARGEMENT...'}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (referralCode) {
                          navigator.clipboard.writeText(referralCode);
                          toast({
                            title: "Code copié !",
                            description: "Votre code de parrainage a été copié dans le presse-papier",
                          });
                        }
                      }}
                    >
                      Copier
                    </Button>
                  </div>
                </div>

                {/* Nombre de parrainés */}
                <div className="flex items-center gap-4">
                  <Users className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm text-muted-foreground">Personnes parrainées</p>
                    <p className="text-2xl font-bold">{referralCount}</p>
                  </div>
                </div>

                <Separator />

                {/* Explication du système */}
                <div className="bg-white p-6 rounded-lg border border-primary/20">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Gift className="w-5 h-5 text-primary" />
                    Comment ça marche ?
                  </h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      • <span className="font-semibold text-foreground">Partagez votre code</span> avec vos proches
                    </p>
                    <p>
                      • <span className="font-semibold text-foreground">Gagnez 1 point</span> pour chaque mois où la personne parrainée reste abonnée
                    </p>
                    <p>
                      • <span className="font-semibold text-foreground">Tous les 6 points</span>, un cadeau surprise est envoyé directement au bénéficiaire de l'abonnement (le grand-parent)
                    </p>
                  </div>
                  <div className="mt-4 p-4 bg-background/50 rounded-lg border border-primary/10">
                    <div className="flex items-center justify-between text-sm mb-3">
                      <span className="text-muted-foreground font-medium">Progression vers le prochain cadeau</span>
                      <span className="font-bold text-primary">{referralPoints} / 6 points</span>
                    </div>
                    
                    {/* Barre de progression visuelle */}
                    <div className="relative">
                      <Progress value={(referralPoints / 6) * 100} className="h-3 mb-4" />
                      
                      {/* Points de progression */}
                      <div className="flex justify-between items-center -mt-2">
                        {[1, 2, 3, 4, 5, 6].map((point) => (
                          <div key={point} className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${
                                referralPoints >= point
                                  ? 'bg-primary border-primary text-primary-foreground shadow-md'
                                  : 'bg-background border-muted text-muted-foreground'
                              }`}
                            >
                              {point === 6 ? (
                                <Gift className="w-4 h-4" />
                              ) : (
                                <span className="text-xs font-bold">{point}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Account;